<template>
  <form-section :title="title">
    <template slot="controls">
      <content-actions
        v-model="value"
        :channel="$route.query.channel"
        :is-content-empty="isContentEmpty"
        import-enabled
        export-enabled
        apply-template-enabled
        copy-enabled
        :model="model"
      />
      <slot name="selected-template-info"></slot>
    </template>
    <el-row>
      <el-col :lg="10" :xl="8">
        <form-item
          prop="email_subject"
          :label="$t('CampaignForm_Email_Field_Subject')"
        >
          <snippet-input
            v-model="value.email_subject"
            :optimove="isOptimove"
            :salesforce="isSalesforce"
            :ai="isAi"
            data-qa="email-title-input"
          />
        </form-item>
        <form-item
          prop="email_from_address"
          :label="$t('CampaignForm_Email_Field_Address')"
        >
          <el-autocomplete
            popper-class="autocomplete-list"
            autocomplete="no"
            v-model="value.email_from_address"
            :fetch-suggestions="querySearchFromAddr"
            @select="handleFromAddr"
            data-qa="email-from-input"
          >
            <template slot-scope="{ item }">
              <div data-qa="autocomplete-item">
                <div>{{ item.value }}</div>
                <div class="desc">{{ item.description }}</div>
              </div>
            </template>
          </el-autocomplete>
        </form-item>
        <form-item
          prop="email_from_name"
          :label="$t('CampaignForm_Email_Field_Name')"
        >
          <el-input
            v-model="value.email_from_name"
            data-qa="email-from-name-input"
          />
        </form-item>
        <form-item
          prop="email_reply_to_address"
          :label="$t('CampaignForm_Email_Field_ReplyToAddress')"
        >
          <el-input
            autocomplete="no"
            v-model="value.email_reply_to_address"
            data-qa="email-reply-to-address-input"
          />
        </form-item>
        <form-item
          prop="email_reply_to_name"
          :label="$t('CampaignForm_Email_Field_ReplyToName')"
        >
          <el-input
            v-model="value.email_reply_to_name"
            data-qa="email-reply-to-name-input"
          />
        </form-item>
        <form-item
          v-if="isAttachmentsAllowed"
          prop="attachment"
          :label="$t('CampaignForm_Email_Field_Attachment')"
        >
          <attachment-upload v-model="value.email.attachments" />
        </form-item>
        <form-item :label="$t('CampaignForm_Email_Header_Unsubscribe_Title')">
          <el-select
            v-model="value.email_unsubscribe"
            data-qa="email-message-type-dropdown"
          >
            <el-option
              :value="1"
              :label="$t('CampaignForm_Email_Header_Unsubscribe_On')"
              data-qa="unsubscribe-option-on"
            />
            <el-option
              :value="0"
              :label="$t('CampaignForm_Email_Header_Unsubscribe_Off')"
              data-qa="unsubscribe-option-off"
            />
          </el-select>
        </form-item>
      </el-col>
      <el-col :lg="14" :xl="16" class="preview">
        <iframe
          v-if="emailPreview"
          :srcdoc="emailPreview"
          class="email-preview"
        ></iframe>
        <div v-else>
          <el-button
            type="primary"
            @click="showBuilder = true"
            data-qa="create-with-builder-btn"
          >
            {{ $t('CampaignForm_Email_Button_CreateWithBuilder') }}
          </el-button>
          <el-button
            type="primary"
            @click="$refs.fileInput.click()"
            data-qa="upload-html-btn"
          >
            {{ $t('CampaignForm_Email_Button_UploadHtml') }}
            <input
              type="file"
              style="display: none;"
              ref="fileInput"
              accept="text/html"
              @change="handleFileUpload()"
            />
          </el-button>
        </div>
        <div class="footer" v-if="value.email_html">
          <el-button
            v-if="value.email_template"
            type="primary"
            @click="showBuilder = true"
            data-qa="edit-email-content-btn"
          >
            {{ $t('CampaignForm_Email_Button_EditWithBuilder') }}
          </el-button>
          <el-button v-else type="primary" @click="showEditor = true">
            {{ $t('CampaignForm_Email_Button_EditHtml') }}
          </el-button>
          <el-button
            type="primary"
            plain
            @click="deleteEmail"
            :disabled="!value.email_html"
            icon="el-icon-delete"
            data-qa="delete-email-content-btn"
          >
            {{ $t('CampaignForm_Email_Button_Delete') }}
          </el-button>
        </div>
      </el-col>
    </el-row>
    <div class="use-deprecated-styles">
      <email-builder
        ref="builder"
        :template="template"
        v-if="showBuilder"
        :on-save="saveBuilder"
        :on-close="closeBuilder"
        :on-send-test="testSend"
        :on-confirm-close="promptCloseBuilder"
        :generate-text-preview="generateTextPreview"
        :flags="{
          voucherCodesAllowed: model.voucher_codes_enabled || false,
          consentPreference: $hasFeature('subscriptionPreferences')
        }"
      />
      <email-html-editor
        ref="editor"
        :html="value.email_html"
        v-if="showEditor"
        :on-save="saveEditor"
        :on-close="closeEditor"
        :on-send-test="testSend"
        :on-confirm-close="promptCloseEditor"
        :generate-text-preview="generateTextPreview"
      />
    </div>
    <test-send-dialog :data="value" :dialog-visible.sync="showTestSendDialog" />
  </form-section>
</template>

<script>
import EmailBuilder from '@/common/components/email/EmailBuilder'
import EmailHtmlEditor from '@/common/components/email/EmailHtmlEditor'
import FormSection from '@/common/components/form/FormSection'
import FormItem from '@/common/components/form/FormItem'
import { languages as languagesList } from '@/modules/campaign-manage/enum/LanguagesEnum'
import TestSendDialog from '@/modules/campaign-manage/components/content/channels/TestSendDialog'
import axios from 'axios/index'
import { $hasFeature } from '@/core/utils/VueAcl'
import { channelTypes } from '@/modules/campaign-manage/enum/Labels'
import AttachmentUpload from '@/common/components/form/AttachmentUpload'
import SnippetInput from '@/modules/campaign-manage/components/SnippetInput'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import ContentActions from '@/modules/campaign-manage/components/ContentActions'
import { flagStatus } from '@/modules/campaign-manage/enum/FlagStatusEnum'
import { EventBus } from '@/core/EventBus'

export default {
  name: 'Email',
  components: {
    ContentActions,
    SnippetInput,
    AttachmentUpload,
    TestSendDialog,
    FormItem,
    FormSection,
    EmailBuilder,
    EmailHtmlEditor
  },
  data() {
    return {
      channelTypes,
      languagesList,
      input: '',
      search: '',
      showTestSendDialog: false,
      showBuilder: false,
      showEditor: false
    }
  },
  props: {
    value: {
      required: true
    },
    title: {
      required: true
    },
    model: {
      type: Object,
      required: true
    }
  },
  mounted() {
    if (this.value.email_html !== '' && this.model.voucher_codes_enabled) {
      this.$set(
        this.value,
        'email_html',
        this.fixBarcodeInHtml(this.value.email_html)
      )

      this.$set(
        this.value,
        'email_template',
        this.fixBarcodeInTemplate(this.value.email_template)
      )
      EventBus.$emit('updateInitialStateMessages', this.model.messages)
    }
  },
  computed: {
    isAi() {
      return this.model.ai_enabled === flagStatus.ENABLED
    },
    isOptimove() {
      return this.model.trigger === campaignTriggerTypes.TRIGGER_OPTIMOVE
    },
    isSalesforce() {
      return this.model.trigger === campaignTriggerTypes.TRIGGER_SALESFORCE
    },
    isContentEmpty() {
      return (
        !this.value.email_html &&
        !this.value.email_subject &&
        !this.value.email_from_name &&
        !this.value.email_reply_to_name
      )
    },
    isAttachmentsAllowed() {
      return $hasFeature('emailAttachments')
    },
    template() {
      return JSON.parse(this.value.email_template)
    },
    emailPreview() {
      if (!this.value.email_template) {
        return this.value.email_html
      }
      try {
        JSON.parse(this.value.email_template)
      } catch (e) {
        return this.value.email_html
      }
      // Recursively search template for replacements in the e-mail body for previewing the HTML.
      let recursiveSearchForReplacements = function(
        elements,
        replacements = []
      ) {
        for (const element in elements) {
          // For dynamic images the source is overridden with a placeholder in previews.
          if (
            element === 'image' &&
            elements[element].src &&
            elements[element].dynamicSrc
          ) {
            replacements.push({
              src: elements[element].src,
              dynamicSrc: elements[element].dynamicSrc
            })
          } else if (
            elements.hasOwnProperty(element) &&
            typeof elements[element] === 'object'
          ) {
            recursiveSearchForReplacements(elements[element], replacements)
          }
        }
        return replacements
      }
      // Apply replacements to HTML preview.
      let previewHtml = this.value.email_html
      recursiveSearchForReplacements(
        JSON.parse(this.value.email_template)
      ).forEach(function(replacement) {
        previewHtml = previewHtml.replace(
          'src="' + replacement.dynamicSrc + '"',
          'src="' + replacement.src + '"'
        )
      })
      return previewHtml
    }
  },
  methods: {
    putCursor(ref) {
      setTimeout(() => {
        this.$refs[ref].focus()
      }, 0)
    },
    handleFromAddr(item) {
      this.value.email_from_address = item.value
      this.value.email_from_name = item.name
      this.value.email_reply_to_name = item.name
      this.value.email_reply_to_address = item.value
    },
    querySearchFromAddr(queryString, cb) {
      axios
        .get(`/email/email-address/autocomplete`, {
          params: {
            project_id: this.$route.query.project_id,
            term: queryString
          }
        })
        .then(({ data }) => {
          cb(data)
        })
    },
    deleteEmail() {
      return this.$msgbox({
        title: this.$t('CampaignForm_Common_Delete_Title'),
        message: this.$t('CampaignForm_Email_Delete_Desc'),
        showCancelButton: true,
        confirmButtonText: this.$t('CampaignForm_Common_Button_Delete'),
        cancelButtonText: this.$t('CampaignForm_Common_Button_Cancel'),
        center: true
      }).then(() => {
        this.value.email_html = null
        this.value.email_template = null
      })
    },
    handleFileUpload() {
      const file = this.$refs.fileInput.files[0]
      const reader = new FileReader()
      reader.addEventListener(
        'load',
        function() {
          this.value.email_html = reader.result
        }.bind(this),
        false
      )

      if (file) {
        if (/\.(htm|html?)$/i.test(file.name)) {
          reader.readAsText(file)
        }
      }
    },
    closeBuilder() {
      this.showBuilder = false
    },
    closeEditor() {
      this.showEditor = false
    },
    testSend(html) {
      this.value.email_html = html
      this.showTestSendDialog = true
    },
    saveEditor(html) {
      this.value.email_template = ''
      this.value.email_html = html
      this.closeEditor()
    },
    saveBuilder(json, html) {
      this.value.email_template = json
      this.value.email_html = html
      this.closeBuilder()
    },
    promptCloseEditor() {
      this.msgBox().then(() => {
        this.closeEditor()
      })
    },
    promptCloseBuilder() {
      this.msgBox().then(() => {
        this.closeBuilder()
      })
    },
    msgBox() {
      return this.$msgbox({
        title: this.$t('CampaignForm_Email_Close_Title'),
        message: this.$t('CampaignForm_Email_Close_Desc'),
        showCancelButton: true,
        confirmButtonText: this.$t('CampaignForm_Common_Button_Close'),
        cancelButtonText: this.$t('CampaignForm_Common_Button_Cancel'),
        center: true
      })
    },
    generateTextPreview(html, success) {
      $.ajax({
        type: 'post',
        url: `/push/campaign/generate-email-text?project_id=${window.xp.project.id}`,
        data: { html: html },
        success: success
      })
    },
    fixBarcodeInHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html')
      for (const barcodeWrapper of doc.querySelectorAll(
        '.remove-before-sending'
      )) {
        if (barcodeWrapper) {
          if (
            barcodeWrapper.style.background === '' &&
            barcodeWrapper.firstElementChild.tagName !== 'SPAN'
          ) {
            barcodeWrapper.style.background = '#FFFFFF'
          }
        }
      }
      return doc.body.innerHTML
    },
    fixBarcodeInTemplate(template) {
      let data = JSON.parse(template)
      let self = this

      function recursiveSearch(data) {
        if (typeof data === 'object' && data !== null) {
          if (
            data.moduleInternal &&
            data.moduleInternal.uid === 'voucher-codes-addon'
          ) {
            data.descriptor.html.html = self.fixBarcodeInHtml(
              data.descriptor.html.html
            )
          }
          for (const key in data) {
            if (data.hasOwnProperty(key)) {
              recursiveSearch(data[key])
            }
          }
        }
      }
      recursiveSearch(data)
      return JSON.stringify(data)
    }
  }
}
</script>

<style scoped lang="scss">
/deep/ .el-row .el-upload {
  width: 100%;
}
.desc {
  margin-top: -12px;
}
.preview {
  background-color: #f8fafc;
  border-radius: 4px;
  min-height: 610px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;

  .email-preview {
    position: absolute;
    background-color: #ffffff;
    width: calc(100% - 88px);
    height: 100%;
    max-height: 490px;
    border: 0;
    filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1));
    left: 0;
    top: 0;
    margin: 44px 44px 0 44px;
    box-sizing: border-box;
  }
  .footer {
    padding-top: 20px;
    display: flex;
    flex: 1;
    padding-bottom: 16px;
    button {
      align-self: flex-end;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/content/channels/Email.vue